import dayjs from 'dayjs'
import { joinURL } from 'ufo'

const MANIFEST_TIMEOUT_MS = 1500
const RELOADED_FOR_KEY = 'core:stale-boot-reload:build'

interface AppPaths {
    baseURL: string
    cdnURL?: string
    buildAssetsDir: string
}

/** The deployed build's id from `builds/latest.json`, or `null` when it cannot be read in time. */
export const fetchLatestBuildId = async (app: AppPaths): Promise<string | null> => {
    // AbortController rather than AbortSignal.timeout, which Safari 15 lacks.
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), MANIFEST_TIMEOUT_MS)
    // The query keeps Cloudflare and the browser from answering with an older copy.
    const url = `${joinURL(app.cdnURL || app.baseURL, app.buildAssetsDir, 'builds/latest.json')}?${dayjs().valueOf()}`

    try {
        const response = await fetch(url, { cache: 'no-store', signal: controller.signal })
        if (!response.ok) return null

        const { id } = await response.json() as { id?: unknown }
        return typeof id === 'string' ? id : null
    }
    catch {
        return null
    }
    finally {
        clearTimeout(timer)
    }
}

/**
 * Records that this tab reloads for `buildId`; `false` when it already did, or when sessionStorage is
 * unavailable and nothing could stop a reload loop.
 */
export const claimReloadFor = (buildId: string): boolean => {
    try {
        if (sessionStorage.getItem(RELOADED_FOR_KEY) === buildId) return false
        sessionStorage.setItem(RELOADED_FOR_KEY, buildId)
        return true
    }
    catch {
        return false
    }
}
